import { useCallback, useEffect, useState } from 'react';
import { App, Form, Input, Modal, Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    contentFilterVersionCreate,
    type ContentFilterPlatform,
} from '@/services/content-filter';

const PLATFORM_OPTIONS: { value: ContentFilterPlatform; labelId: string }[] = [
    { value: 'ios', labelId: 'contentFilter.versions.platform.ios' },
    { value: 'android', labelId: 'contentFilter.versions.platform.android' },
];

/** builder 号：纯数字，1~10 位 */
const BUILDER_PATTERN = /^\d{1,10}$/;

export type AddVersionModalProps = {
    open: boolean;
    platform: ContentFilterPlatform;
    onClose: () => void;
    onSuccess: () => void;
};

const AddVersionModal: React.FC<AddVersionModalProps> = ({ open, platform, onClose, onSuccess }) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const [form] = Form.useForm<{ builder: string; platform: ContentFilterPlatform }>();

    const [submitting, setSubmitting] = useState(false);

    const resetForm = useCallback(() => {
        form.resetFields();
    }, [form]);

    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, resetForm]);

    const handleSubmit = useCallback(async () => {
        const values = await form.validateFields();

        setSubmitting(true);
        try {
            const response = await contentFilterVersionCreate({
                builder: values.builder,
                platform: values.platform,
            });
            const { data } = response;

            if (data.code !== 200) {
                message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                return;
            }

            message.success(formatMessage({ id: 'contentFilter.message.versionCreated' }));
            onSuccess();
            onClose();
        } catch (error: any) {
            message.error(
                error?.response?.data?.msg ||
                    error?.message ||
                    formatMessage({ id: 'contentFilter.message.saveFailed' }),
            );
        } finally {
            setSubmitting(false);
        }
    }, [form, formatMessage, message, onClose, onSuccess]);

    return (
        <Modal
            open={open}
            title={<FormattedMessage id="contentFilter.modal.createTitle" />}
            width={480}
            destroyOnClose
            confirmLoading={submitting}
            okText={<FormattedMessage id="contentFilter.modal.submit" />}
            cancelText={<FormattedMessage id="b.cancel" />}
            onCancel={onClose}
            onOk={handleSubmit}
        >
            <Form form={form} layout="vertical" initialValues={{ platform }}>
                <Form.Item
                    label={<FormattedMessage id="contentFilter.modal.platform" />}
                    name="platform"
                    rules={[{ required: true }]}
                >
                    <Select
                        options={PLATFORM_OPTIONS.map((option) => ({
                            label: formatMessage({ id: option.labelId }),
                            value: option.value,
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    label={<FormattedMessage id="contentFilter.modal.versionName" />}
                    name="builder"
                    rules={[
                        { required: true },
                        {
                            pattern: BUILDER_PATTERN,
                            message: formatMessage({ id: 'contentFilter.modal.builderPattern' }),
                        },
                    ]}
                    extra={<FormattedMessage id="contentFilter.modal.hint" />}
                    validateFirst
                >
                    <Input
                        placeholder={formatMessage({ id: 'contentFilter.modal.versionPlaceholder' })}
                        allowClear
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddVersionModal;
